import { useRef, useState } from 'react'

import { accumulateStreamStep } from '~pages/AISettingsScreen/components/ChatStreamSteps'
import { type ChatStreamStatusStats, type ChatStreamStepEntry } from '~types'

import { type StreamStatusPhase } from './useStreamAIResponse'

interface StreamStepResponse {
  step: string
  status_phase?: StreamStatusPhase
  status_stats?: ChatStreamStatusStats
}

// The minimal message shape needed to key/archive a past turn's expanded panel state
export interface StreamStepMessage {
  id?: string | null
  sender: string
  streamSteps?: unknown[]
}

export interface UseChatStreamStepsResult {
  liveSteps: ChatStreamStepEntry[]
  stepsExpanded: boolean
  setStepsExpanded: (expanded: boolean) => void
  historicalStepsExpanded: Record<string, boolean>
  getMessageKey: (message: StreamStepMessage, index: number) => string
  setHistoricalStepsExpanded: (key: string, expanded: boolean) => void
  resetHistoricalStepsExpanded: () => void
  // Feeds one raw stream event into the shared accumulator; call for every event on the stream
  onStreamResponse: (response: StreamStepResponse) => void
  // Snapshot of the steps accumulated so far, safe to read synchronously (e.g. when flushing a message)
  getSteps: () => ChatStreamStepEntry[]
  // Archives the previous turn's expanded state (if it had steps), then resets for a new turn
  beginTurn: (messages: readonly StreamStepMessage[]) => void
  // Resets for a new turn without archiving (the just-finished turn already kept its own expanded state)
  endTurn: () => void
}

// Single source of truth for the chat stream steps panel state, shared across chat/search/assistant
export const useChatStreamSteps = (): UseChatStreamStepsResult => {
  const stepsRef = useRef<ChatStreamStepEntry[]>([])
  const [liveSteps, setLiveSteps] = useState<ChatStreamStepEntry[]>([])
  const [stepsExpanded, setStepsExpanded] = useState(false)
  const [historicalStepsExpanded, setHistoricalStepsExpandedState] = useState<
    Record<string, boolean>
  >({})

  const getMessageKey = (message: StreamStepMessage, index: number): string =>
    message.id || `temp-${index}`

  const setHistoricalStepsExpanded = (key: string, expanded: boolean): void => {
    setHistoricalStepsExpandedState((prev) => ({ ...prev, [key]: expanded }))
  }

  // Clears all archived per-message expand state; call when messages are wiped (e.g. chat reset)
  // so a fresh message can't inherit a stale entry via a reused temp-${index} key
  const resetHistoricalStepsExpanded = (): void => {
    setHistoricalStepsExpandedState({})
  }

  const onStreamResponse = (response: StreamStepResponse): void => {
    const updated = accumulateStreamStep(stepsRef.current, response)
    if (updated !== stepsRef.current) {
      stepsRef.current = updated
      setLiveSteps(updated)
    }
  }

  const getSteps = (): ChatStreamStepEntry[] => stepsRef.current

  const endTurn = (): void => {
    stepsRef.current = []
    setLiveSteps([])
    setStepsExpanded(false)
  }

  const beginTurn = (messages: readonly StreamStepMessage[]): void => {
    const lastIndex = messages.length - 1
    const last = messages[lastIndex]
    if (last?.sender === 'AI' && last.streamSteps?.length) {
      setHistoricalStepsExpanded(getMessageKey(last, lastIndex), stepsExpanded)
    }
    endTurn()
  }

  return {
    liveSteps,
    stepsExpanded,
    setStepsExpanded,
    historicalStepsExpanded,
    getMessageKey,
    setHistoricalStepsExpanded,
    resetHistoricalStepsExpanded,
    onStreamResponse,
    getSteps,
    beginTurn,
    endTurn,
  }
}
